/**
 * Sponsor details form — second step of the sponsorship sub-flow.
 *
 * Collects the sponsor's name + email (required), optional contact/phone,
 * and any fulfillment fields the tier declares (logo URL, website, etc.).
 * No OTP — sponsorship purchase is a guest transaction. Details are
 * stashed in FlowContext and carried into payment.
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { EmbedClient, SponsorCheckoutBody, SponsorshipFulfillmentField } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { isInApp } from '@/inApp';
import './SponsorDetailsPage.css';

// Field types a sponsor can type into a text box right here at checkout.
// Anything else (logo/image, file uploads) can't be collected inline — we
// defer those to the post-purchase fulfillment link that gets emailed, so
// the sponsor isn't blocked trying to paste a logo into a text field.
const INLINE_FIELD_TYPES = new Set([
  'text', 'textarea', 'url', 'email', 'tel', 'phone', 'number', 'string',
]);

function isInlineField(f: SponsorshipFulfillmentField): boolean {
  return INLINE_FIELD_TYPES.has((f.type || 'text').toLowerCase());
}

interface Props {
  // Used for the optional logo upload (stored and cleaned for the preview).
  client?: EmbedClient;
}

// Max raw logo size before base64 (~1.5 MB → ~2 MB data URI).
const MAX_LOGO_BYTES = 1.5 * 1024 * 1024;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function SponsorDetailsPage({ client }: Props) {
  const { eventId, sponsorshipId } = useParams<{ eventId: string; sponsorshipId: string }>();
  const navigate = useFlowNavigate();
  const { selectedSponsorship, setSponsorForm } = useFlow();

  const [sponsorName, setSponsorName] = useState('');
  const [sponsorEmail, setSponsorEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [fulfillment, setFulfillment] = useState<Record<string, string>>({});
  // Digital Contact Card addon (+$10, offered on every tier): the
  // sponsor's offer or contact card on golfers' phones during the round.
  const [wantAddon, setWantAddon] = useState(false);
  const [addonFlavor, setAddonFlavor] = useState<'offer' | 'card'>('offer');
  const [offerHeadline, setOfferHeadline] = useState('');
  const [offerCode, setOfferCode] = useState('');
  const [offerExpiry, setOfferExpiry] = useState('');
  const [cardMemo, setCardMemo] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Optional logo on checkout (owner direction, 2026-10): stored + cleaned
  // on pick so the sponsor sees it the way golfers will. Skipped logos are
  // still asked for in the post-purchase email.
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoCleaned, setLogoCleaned] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const tier = selectedSponsorship;

  // Split declared fulfillment fields: the ones we can collect inline (text,
  // url, etc.) vs. asset uploads (logo/image, files) we hand off to the
  // emailed fulfillment link after purchase.
  const inlineFields = useMemo(
    () => (tier ? tier.fulfillment_fields.filter(isInlineField) : []),
    [tier],
  );
  const allDeferred = useMemo(
    () => (tier ? tier.fulfillment_fields.filter((f) => !isInlineField(f)) : []),
    [tier],
  );
  const logoField = allDeferred.find((f) => ['image', 'image_url'].includes((f.type || '').toLowerCase()));
  const deferredFields = allDeferred.filter((f) => !(f === logoField && logoUrl));

  // Only inline fields can block submission — deferred assets are provided
  // later via the emailed link, so a required logo doesn't trap the sponsor.
  const missingRequiredFulfillment = useMemo(() => {
    return inlineFields.some(
      (f) => f.required && !(fulfillment[f.key] ?? '').trim(),
    );
  }, [inlineFields, fulfillment]);

  // If we got here without a selected tier (e.g. reload mid-flow), bounce
  // back to the tier list.
  if (!tier) {
    navigate(`/events/${eventId}/sponsor`, { replace: true });
    return null;
  }

  const onPickLogo = async (file: File | undefined) => {
    setError(null);
    if (!file || !eventId) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError('That logo is too large. Please use an image under 1.5 MB.');
      return;
    }
    setLogoBusy(true);
    try {
      const dataUri = await readAsDataUrl(file);
      setLogoPreview(dataUri);
      if (client) {
        try {
          const out = await client.uploadSponsorLogo(eventId, dataUri);
          setLogoUrl(out.logo_url);
          setLogoCleaned(!!out.clean);
          if (out.clean) setLogoPreview(out.clean.url);
          return;
        } catch {
          // Upload unavailable: send the image itself; the server stores
          // and cleans it after purchase.
        }
      }
      setLogoUrl(dataUri);
      setLogoCleaned(false);
    } catch {
      setError('Could not read that file. Please try another.');
    } finally {
      setLogoBusy(false);
    }
  };

  const clearLogo = () => {
    setLogoUrl('');
    setLogoPreview('');
    setLogoCleaned(false);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sponsorName.trim() || !sponsorEmail.trim()) {
      setError('Name and email are required.');
      return;
    }
    if (missingRequiredFulfillment) {
      setError('Please fill in the required sponsorship details.');
      return;
    }
    if (wantAddon && addonFlavor === 'offer' && !offerHeadline.trim()) {
      setError('Add the offer text for your Digital Contact Card, or switch it off.');
      return;
    }
    // Card content rides fulfillment_data in the same shape the golf app
    // sends; the server drops these keys unless the addon is paid.
    const data: Record<string, unknown> = { ...fulfillment };
    if (logoField && logoUrl) data[logoField.key] = logoUrl;
    if (wantAddon) {
      if (addonFlavor === 'card') {
        data.contact_card = {
          memo: cardMemo.trim() || undefined,
          phone: phone.trim() || undefined,
          email: sponsorEmail.trim(),
        };
      } else {
        data.coupon = {
          headline: offerHeadline.trim(),
          code: offerCode.trim() || undefined,
          expiry: offerExpiry || undefined,
          redeem_type: 'show',
        };
      }
    }
    const body: SponsorCheckoutBody = {
      sponsor_name: sponsorName.trim(),
      sponsor_email: sponsorEmail.trim(),
      sponsor_contact_name: contactName.trim() || undefined,
      sponsor_phone: phone.trim() || undefined,
      fulfillment_data: Object.keys(data).length ? data : undefined,
      offer_addon: wantAddon,
    };
    setSponsorForm(body);
    navigate(`/events/${eventId}/sponsor/${sponsorshipId}/payment`);
  };

  return (
    <div className="tn-sponsor-details">
      <h1 className="tn-sponsor-details__title">{tier.tier_name}</h1>
      <p className="tn-sponsor-details__subtitle">Tell us who's sponsoring.</p>

      <form className="tn-sponsor-details__form" onSubmit={onSubmit}>
        <label className="tn-field">
          <span className="tn-field__label">Sponsor / business name *</span>
          <input
            className="tn-field__input"
            value={sponsorName}
            onChange={(e) => setSponsorName(e.target.value)}
            autoComplete="organization"
            required
          />
        </label>

        <label className="tn-field">
          <span className="tn-field__label">Email *</span>
          <input
            className="tn-field__input"
            type="email"
            value={sponsorEmail}
            onChange={(e) => setSponsorEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>

        <label className="tn-field">
          <span className="tn-field__label">Contact name</span>
          <input
            className="tn-field__input"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            autoComplete="name"
          />
        </label>

        <label className="tn-field">
          <span className="tn-field__label">Phone</span>
          <input
            className="tn-field__input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        </label>

        {logoField ? (
          <div className="tn-field">
            <span className="tn-field__label">
              Logo <span className="tn-sponsor-logo__optional">(optional, you can add it later)</span>
            </span>
            {logoPreview ? (
              <div className="tn-sponsor-logo">
                {/* The logo at its own shape, no tile (the app's look). */}
                <img src={logoPreview} alt="Your logo" />
                <div className="tn-sponsor-logo__actions">
                  <label className="tn-sponsor-logo__link">
                    Replace
                    <input
                      className="tn-sponsor-logo__file"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        void onPickLogo(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <button type="button" className="tn-sponsor-logo__link tn-sponsor-logo__link--muted" onClick={clearLogo}>
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <label className="tn-sponsor-logo__drop">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <path d="M17 8l-5-5-5 5" />
                  <path d="M12 3v12" />
                </svg>
                <span>{logoBusy ? 'Uploading…' : 'Upload your logo'}</span>
                <input
                  className="tn-sponsor-logo__file"
                  type="file"
                  accept="image/*"
                  disabled={logoBusy}
                  onChange={(e) => {
                    void onPickLogo(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
            <span className="tn-sponsor-addon__desc">
              {logoPreview && logoCleaned
                ? 'Looks good: extra space trimmed. Plain white backgrounds are removed; colored badges keep theirs.'
                : 'PNG or JPG. We trim extra space and remove plain white backgrounds so it looks clean in the app.'}
            </span>
          </div>
        ) : null}

        {inlineFields.map((f) => (
          <label className="tn-field" key={f.key}>
            <span className="tn-field__label">
              {f.label}{f.required ? ' *' : ''}
            </span>
            <input
              className="tn-field__input"
              value={fulfillment[f.key] ?? ''}
              onChange={(e) =>
                setFulfillment((prev) => ({ ...prev, [f.key]: e.target.value }))
              }
              placeholder={f.hint ?? undefined}
            />
          </label>
        ))}

        {/* Never sold inside the app's in-app browser (Apple: digital goods
            bought in-app need in-app purchase). See src/inApp.ts. */}
        {tier.addon_available && !isInApp() ? (
          <div className={`tn-sponsor-addon${wantAddon ? ' tn-sponsor-addon--on' : ''}`}>
            <label className="tn-sponsor-addon__toggle">
              <input
                type="checkbox"
                checked={wantAddon}
                onChange={(e) => setWantAddon(e.target.checked)}
              />
              <span className="tn-sponsor-addon__toggle-copy">
                <span className="tn-sponsor-addon__title">
                  Add to the Digital Swag Bag
                  <span className="tn-sponsor-addon__price">
                    +${(((tier.addon_price_cents ?? 1000)) / 100).toFixed(0)}
                  </span>
                </span>
                <span className="tn-sponsor-addon__desc">
                  Put an offer or your contact card in every golfer’s Swag Bag in
                  the Golf Fore Good app. They see it during the round and keep
                  it after the event, so they can come back to you.
                </span>
              </span>
            </label>
            {wantAddon ? (
              <div className="tn-sponsor-addon__body">
                <div className="tn-sponsor-addon__flavors" role="radiogroup" aria-label="Card style">
                  <button
                    type="button"
                    className={`tn-sponsor-addon__flavor${addonFlavor === 'offer' ? ' tn-sponsor-addon__flavor--on' : ''}`}
                    onClick={() => setAddonFlavor('offer')}
                  >
                    Offer
                  </button>
                  <button
                    type="button"
                    className={`tn-sponsor-addon__flavor${addonFlavor === 'card' ? ' tn-sponsor-addon__flavor--on' : ''}`}
                    onClick={() => setAddonFlavor('card')}
                  >
                    Contact card
                  </button>
                </div>
                {addonFlavor === 'offer' ? (
                  <>
                    <label className="tn-field">
                      <span className="tn-field__label">Your offer *</span>
                      <input
                        className="tn-field__input"
                        value={offerHeadline}
                        maxLength={120}
                        onChange={(e) => setOfferHeadline(e.target.value)}
                        placeholder="Show this card for 10% off lunch"
                      />
                    </label>
                    <label className="tn-field">
                      <span className="tn-field__label">Promo code</span>
                      <input
                        className="tn-field__input"
                        value={offerCode}
                        maxLength={40}
                        onChange={(e) => setOfferCode(e.target.value)}
                        placeholder="Optional"
                      />
                    </label>
                    <label className="tn-field">
                      <span className="tn-field__label">Good through</span>
                      <input
                        className="tn-field__input"
                        type="date"
                        value={offerExpiry}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setOfferExpiry(e.target.value)}
                      />
                      <span className="tn-sponsor-addon__desc">
                        Optional. After this date the offer shows as ended in golfers’ Swag Bags and Wallets.
                      </span>
                    </label>
                  </>
                ) : (
                  <label className="tn-field">
                    <span className="tn-field__label">Card note</span>
                    <input
                      className="tn-field__input"
                      value={cardMemo}
                      onChange={(e) => setCardMemo(e.target.value)}
                      placeholder="Ask for Jordan."
                    />
                  </label>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {deferredFields.length > 0 ? (
          <p className="tn-sponsor-details__deferred-note">
            After you complete your purchase, we'll email you a secure link to
            upload your {deferredFields.map((f) => f.label.replace(/^your\s+/i, '').toLowerCase()).join(', ')}.
          </p>
        ) : null}

        {error ? <div role="alert" className="tn-sponsor-details__error">{error}</div> : null}

        <button type="submit" className="tn-btn tn-btn--primary">
          Continue to payment
        </button>
      </form>
    </div>
  );
}
